// useReportPDFPrinter.js

import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Módulo para dibujar tablas fácilmente
import { formatCurrency, formatDate } from '@/composables/useUtils';
/**
 * Genera un PDF estructurado (Formato Pro) para cualquier reporte tabular.
 * @param {Array} reportData - Los datos del reporte (filas).
 * @param {string} reportType - El tipo de reporte ('date', 'client', etc.).
 * @param {Object} companyConfig - Configuración de la empresa.
 * @param {string} startDate - Fecha de inicio del reporte.
 * @param {string} endDate - Fecha de fin del reporte.
 */
//CORREGIDO: La función principal NO es async
export function useReportPDFPrinter(reportData, reportType, companyConfig, startDate, endDate, extraData = null) {

    return new Promise((resolve) => { // La función ejecutora NO es async

        // Función interna ASYNC para manejar el await del logo
        const doPrintAsync = async () => {
            const isDetailReport = (reportType === 'date' || reportType === 'amounts');
            const paperFormat = isDetailReport ? 'legal' : 'letter';

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: paperFormat
            });

            const currencySymbol = companyConfig?.currency_symbol || '$';
            const currencyCode = companyConfig?.currency_code || 'USD';
            const companyName = companyConfig?.emitter_name || 'Sistema POS';
            const logoUrl = companyConfig?.logo_url || '';
            const margin = 15;
            const pageWidth = doc.internal.pageSize.getWidth();
            let startY = 15;

            // --- Carga y Dibujo del Logo (ASYNC) ---
            if (logoUrl) {
                try {
                    const imageBase64 = await new Promise((res) => {
                        const img = new Image();
                        img.crossOrigin = 'Anonymous';
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width;
                            canvas.height = img.height;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);
                            res(canvas.toDataURL('image/png'));
                        };
                        img.onerror = () => res(null);
                        img.src = logoUrl;
                    });

                    if (imageBase64) {
                        const logoWidth = 35;
                        const logoHeight = 15;
                        doc.addImage(imageBase64, 'PNG', margin, startY, logoWidth, logoHeight);
                        startY += logoHeight + 5;
                    }
                } catch (e) {
                    console.error("Fallo al añadir logo al PDF:", e);
                    startY += 5;
                }
            } else {
                startY += 5;
            }

            // --- 1. CONFIGURACIÓN DE COLUMNAS DINÁMICAS ---
            let columns = [];
            let title = "REPORTE DE FACTURACIÓN";

            if (reportType === 'date' || reportType === 'amounts') {
                title = "REPORTE DETALLADO POR FECHAS/MONTOS";
                columns = [
                    { header: '#', dataKey: 'row_number', width: 8 },
                    { header: 'TIPO', dataKey: 'invoice_type', width: 15 },
                    { header: 'NÚMERO', dataKey: 'full_number', width: 25 },
                    { header: 'FECHA', dataKey: 'date_issued', width: 15 },
                    { header: 'CLIENTE', dataKey: 'client_name', width: 30 },
                    { header: 'NIT', dataKey: 'client_nit', width: 20 },
                    { header: 'ASESOR', dataKey: 'created_by_username', width: 18 },
                    { header: 'BRUTO', dataKey: 'sub_total_brut' },
                    { header: 'DESC.', dataKey: 'total_discount' },
                    { header: 'SUBTOTAL', dataKey: 'subtotal' },
                    { header: 'IMPUESTO', dataKey: 'total_taxes' },
                    { header: 'TOTAL VENTA', dataKey: 'total' },
                    { header: 'ESTADO', dataKey: 'state', width: 15 },
                ];

            } else if (reportType === 'client') {
                title = "REPORTE RESUMEN POR CLIENTE";
                columns = [
                    { header: '#', dataKey: 'row_number', width: 10 },
                    { header: 'CLIENTE', dataKey: 'client_name' },
                    { header: 'NIT', dataKey: 'client_nit' },
                    { header: 'VENTAS TOTALES', dataKey: 'total_spent' },
                    { header: 'CANT. FACTURAS', dataKey: 'total_invoices' },
                ];
            } else if (reportType === 'advisor') {
                title = "REPORTE RESUMEN POR ASESOR";
                columns = [
                    { header: '#', dataKey: 'row_number', width: 10 },
                    { header: 'ASESOR (USER)', dataKey: 'created_by_username' },
                    { header: 'NOMBRE COMPLETO', dataKey: 'created_by_fullname' },
                    { header: 'VENTAS TOTALES', dataKey: 'total_sales' },
                    { header: 'CANT. FACTURAS', dataKey: 'total_invoices' },
                ];
            } else if (reportType === 'product') {
                title = "REPORTE RESUMEN POR PRODUCTO";
                columns = [
                    { header: '#', dataKey: 'row_number', width: 10 },
                    { header: 'CÓDIGO', dataKey: 'code' },
                    { header: 'PRODUCTO/SERVICIO', dataKey: 'product_service' },
                    { header: 'TOTAL UNIDADES', dataKey: 'total_units' },
                    { header: 'TOTAL RECAUDADO', dataKey: 'total_revenue' },
                ];
            }else if (reportType === 'location') {
                title = "REPORTE RESUMEN POR DEPARTAMENTO";
                columns = [
                    { header: '#', dataKey: 'row_number', width: 10 },
                    { header: 'DEPARTAMENTO', dataKey: 'department_name' },
                    { header: 'FACTURAS EMITIDAS', dataKey: 'invoices' },
                    { header: 'TOTAL RECAUDADO', dataKey: 'total_sales' },
                ];
            }else if (reportType === 'dte_01') {
                title = "LIBRO DE VENTAS A CONSUMIDORES";
                columns = [
                    { header: '#', dataKey: 'row_number' },
                    { header: 'FECHA', dataKey: 'report_date' },
                    { header: 'CODIGO GENERACION DEL', dataKey: 'first_code_gen'},
                    { header: 'CODIGO GENERACION AL', dataKey: 'last_code_gen'},
                    { header: 'NUMERO DE CONTROL DEL', dataKey: 'first_full_number'},
                    { header: 'NUMERO DE CONTROL AL', dataKey: 'last_full_number'},
                    { header: 'EXCENTAS', dataKey: 'total_excent'},
                    { header: 'GRAVADAS', dataKey: 'total_writted'},
                    { header: 'EXPORT', dataKey: 'total_export'},
                    { header: 'TOTAL VENTA', dataKey: 'total_sales'},
                    { header: 'TERCEROS', dataKey: 'total_thirds'},
                ];
            }else if (reportType === 'dte_03') {
                title = "LIBRO DE VENTAS A CONTRIBUYENTES";
                columns = [
                    { header: '#', dataKey: 'row_number' },
                    { header: 'FECHA', dataKey: 'fecha_emision' },
                    { header: 'CODIGO DE GENERACION', dataKey: 'codigo_generacion' },
                    { header: 'N.R.C.', dataKey: 'nrc' },
                    { header: 'NOMBRE DEL CLIENTE', dataKey: 'nombre_cliente' },
                    { header: 'EXCENTAS', dataKey: 'venta_exenta'},
                    { header: 'GRAVADAS', dataKey: 'venta_gravada'},
                    { header: 'DEBITO', dataKey: 'percepcion_iva'},
                    { header: 'TOTAL VENTA', dataKey: 'total_venta',},
                    { header: 'PERCEPCIÓN IVA', dataKey: 'retencion_iva'},
                    { header: 'RETENCIÓN IVA', dataKey: 'retencion_iva'},
                ];
            }

            // --- 2. PREPARACIÓN DE DATOS Y TOTALIZACIÓN ---
            let finalBody = [];
            let totalSales = 0;
            let totalDiscount = 0;
            let totalTaxes = 0;
            let totalExcent=0;
            let totalGrav=0;
            let totalDeb=0;
            let totalRet=0;
            let totalSubtotalNeto = 0;
            let totalSubtotalBruto = 0;
            const numColumns = columns.length;

            reportData.forEach((row, index) => {

                // USAMOS formatUtils para FECHAS
                if (row.date_issued) {
                    row.date_issued = formatDate(row.date_issued);
                }

                 if (reportType !== 'date' && reportType !== 'amounts') {
                     row.row_number = index + 1;
                 }

                 if (reportType === 'date' || reportType === 'amounts') {
                     row.row_number = index + 1;

                     const total = parseFloat(row.total || 0);
                     totalSales += total;
                     totalDiscount += parseFloat(row.total_discount || 0);
                     totalTaxes += parseFloat(row.total_taxes || 0);
                     totalSubtotalNeto += parseFloat(row.subtotal || 0);
                     totalSubtotalBruto += parseFloat(row.sub_total_brut || 0);
                 }else if(reportType === 'dte_03' ){
                    const total= parseFloat(row.total_venta)
                    totalSales += total;
                    totalTaxes += parseFloat(row.percepcion_iva || 0);
                    totalExcent += parseFloat(row.total_excent || 0);
                    totalGrav += parseFloat(row.venta_gravada || 0);
                    totalDeb += parseFloat(row.percepcion_iva || 0);
                    totalRet += parseFloat(row.retencion_iva || 0);
                 } else {
                     totalSales += parseFloat(row.total_spent || row.total_sales || row.total_revenue || 0);
                 }

                // 🛑 USAMOS formatUtils para MONEDAS 🛑
                Object.keys(row).forEach(key => {
                    if (key.includes('total') || key.includes('subtotal') || key.includes('taxes') || key.includes('discount') || key.includes('revenue')) {
                        row[key] = formatCurrency(row[key], currencyCode, currencySymbol);
                    }
                });
                finalBody.push(row);
            });

            // --- 3. TÍTULO Y CABECERA (MetaData) ---
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.text(title, pageWidth - margin, 15, { align: 'right' });

            doc.autoTable({
                head: [],
                body: [
                    [`Empresa: ${companyName}`],
                    // 🛑 Aplicar formato de fecha a los metadatos del período 🛑
                    [`Período: ${formatDate(startDate)} hasta ${formatDate(endDate)}`],
                    [`Moneda: ${currencyCode} (${currencySymbol})`]
                ],
                theme: 'plain',
                startY: startY,
                margin: { left: margin, right: margin },
                styles: { fontSize: 8, cellPadding: 1 },
            });

            const safeStartY = (doc.lastAutoTable && doc.lastAutoTable.finalY)
                               ? doc.lastAutoTable.finalY + 5
                               : startY + 5;

            // --- Lógica para el Pie de Página Dinámico ---
            let footData = [];

            if (reportType === 'date' || reportType === 'amounts') {
                footData = new Array(numColumns).fill('');
                // Formateamos los totales en el pie de página
                footData[6] = 'TOTALES:';
                footData[7] = formatCurrency(totalSubtotalBruto, currencyCode, currencySymbol);
                footData[8] = formatCurrency(totalDiscount, currencyCode, currencySymbol);
                footData[9] = formatCurrency(totalSubtotalNeto, currencyCode, currencySymbol);
                footData[10] = formatCurrency(totalTaxes, currencyCode, currencySymbol);
                footData[11] = formatCurrency(totalSales, currencyCode, currencySymbol);

            } else if(reportType=='dte_01'){
                footData[5] = 'TOTALES:';
                footData[6] = formatCurrency(totalExcent, currencyCode, currencySymbol);
                footData[7] = formatCurrency(totalSales, currencyCode, currencySymbol);
                footData[8] = formatCurrency(totalDeb, currencyCode, currencySymbol);
                footData[9] = formatCurrency(totalSales, currencyCode, currencySymbol);
                //footData[9] = formatCurrency(totalTaxes, currencyCode, currencySymbol);
                footData[10] = formatCurrency(totalRet, currencyCode, currencySymbol);
            } else if(reportType=='dte_03'){
                footData[4] = 'TOTALES:';
                footData[5] = formatCurrency(totalExcent, currencyCode, currencySymbol);
                footData[6] = formatCurrency(totalGrav, currencyCode, currencySymbol);
                footData[7] = formatCurrency(totalDeb, currencyCode, currencySymbol);
                footData[8] = formatCurrency(totalSales, currencyCode, currencySymbol);
                footData[9] = formatCurrency(totalRet, currencyCode, currencySymbol);
                footData[10] = formatCurrency(totalRet, currencyCode, currencySymbol);
            } else {
                // Reportes de Resumen (Totalizar en la penúltima columna)
                footData = new Array(numColumns).fill('');
                footData[numColumns - 2] = 'TOTAL GENERAL:';
                footData[numColumns - 1] = formatCurrency(totalSales, currencyCode, currencySymbol);
            }


            // --- 4. DIBUJAR TABLA DE DATOS ---
            doc.autoTable({
                columns: columns,
                body: finalBody,
                startY: safeStartY,
                margin: { left: margin, right: margin },
                theme: 'striped',
                styles: { fontSize: 7, cellPadding: 1, overflow: 'linebreak' },
                headStyles: { fillColor: [52, 73, 94] },
                didDrawPage: (data) => {
                    doc.setFontSize(8);
                    doc.text(`Página ${data.pageNumber} de ${doc.getNumberOfPages()}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 5, { align: 'right' });
                },

                // Pie de página de la tabla (Adaptativo)
                foot: [footData],
                footStyles: { fontStyle: 'bold', fillColor: [200, 200, 200], fontSize:7,textColor: [0, 0, 0] }
            });

            if (reportType === 'dte_03') {
                // Obtenemos los totales de Consumidor Final desde el objeto extraData que viene de la API
                const cfGravadas = extraData?.cf_resumen?.gravadas || 0;
                const cfDebito = extraData?.cf_resumen?.debito || 0;

                // Determinamos la posición Y basándonos en la tabla anterior
                const summaryY = doc.lastAutoTable.finalY + 10;

                doc.setFontSize(10).setFont(undefined, 'bold');
                doc.text("Resumen Operaciones", margin, summaryY);

                doc.autoTable({
                    startY: summaryY + 2,
                    margin: { left: margin },
                    tableWidth: pageWidth / 1.5, // Ocupa una parte proporcional del ancho legal/landscape
                    head: [['Detalle', 'Gravadas', 'Exportaciones', 'Debito Fiscal', 'IVA Percibido', 'IVA Retenido']],
                    body: [
                        ['Consumidor Final', formatCurrency(cfGravadas, currencyCode, currencySymbol), '0.00', formatCurrency(cfDebito, currencyCode, currencySymbol), '0.00', '0.00'],
                        ['Contribuyentes', formatCurrency(totalGrav, currencyCode, currencySymbol), '0.00', formatCurrency(totalDeb, currencyCode, currencySymbol), '0.00', formatCurrency(totalRet, currencyCode, currencySymbol)],
                        ['Ventas a Cta. de Terceros', '0.00', '0.00', '0.00', '0.00', '0.00'],
                        [{ content: 'Total', styles: { fontStyle: 'bold' } },
                         formatCurrency(parseFloat(cfGravadas) + totalGrav, currencyCode, currencySymbol),
                         '0.00',
                         formatCurrency(parseFloat(cfDebito) + totalDeb, currencyCode, currencySymbol),
                         '0.00',
                         formatCurrency(totalRet, currencyCode, currencySymbol)]
                    ],
                    theme: 'grid',
                    styles: { fontSize: 7, cellPadding: 1 },
                    headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
                });
            }

            if (reportType === 'dte_01' || reportType === 'dte_03') {
                const finalY = doc.lastAutoTable.finalY + 40; // Espacio después de la tabla
                const pageHeight = doc.internal.pageSize.getHeight();

                // Verificar si hay espacio en la página actual o necesitamos una nueva
                let signatureY = finalY;
                if (signatureY + 30 > pageHeight) {
                    doc.addPage();
                    signatureY = 30;
                }

                const lineLength = 60;
                const centerX = pageWidth / 2;
                const lineStartX = centerX - (lineLength / 2);

                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');

                // Dibujar línea de firma
                doc.setLineWidth(0.5);
                doc.line(lineStartX, signatureY, lineStartX + lineLength, signatureY);

                // Texto debajo de la línea
                doc.text("Nombre y Firma del Contador", centerX, signatureY + 5, { align: 'center' });

                /* doc.setFont(undefined, 'normal');
                doc.setFontSize(8);
                doc.text("Nombre y Firma del Contador", centerX, signatureY + 9, { align: 'center' }); */
            }

            // Abrir PDF (Compatible con Safari)
            const fullNumber = `Reporte_${reportType}_${endDate}`;
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.download = `${fullNumber}.pdf`;
            a.click();
            URL.revokeObjectURL(blobUrl);

            resolve();
        };

        //Ejecutar la función interna async
        doPrintAsync();
    });
}